import * as React from 'react';
import { connect } from 'react-redux';

import '../Main.scss';

import { Button, Notification } from '../design-system/components';
import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator';

import { PortalTextSubmissionDetails } from '../../Models/Api/strongbox.financialportal';
import { FieldGuideStatusResult } from '../../Services/FieldGuideService';
import { ApplicationState, SimplifiedThunkDispatch } from '../../Store';
import {
    FieldGuideConnectionState,
    GetFieldGuideConnectionState,
    GetFieldGuideReconnectMessage,
    KnownAction as FieldGuideKnownAction,
    actionCreators as FieldGuideStoreActions,
} from '../../Store/FieldGuide';
import { GetPortalText } from '../../Store/PortalAssets';

import { ConnectStep } from './ConnectStep';

type ConnectingStepPhase = 'checking' | 'ready' | 'unavailable' | 'forbidden';

type InjectedReduxState = {
    connectionState: FieldGuideConnectionState;
    reconnectMessage: string | undefined;
    text?: PortalTextSubmissionDetails;
};

type InjectedActionCreators = {
    // Dispatched directly, not bound, so the thunk's result reaches the caller to render unavailable/forbidden.
    CheckConnectionStatus: (signal?: AbortSignal) => Promise<FieldGuideStatusResult>;
};

type ConnectingStepProps = {
    connectedContent: React.ReactNode;
};

type Props = InjectedReduxState & InjectedActionCreators & ConnectingStepProps;

export const ConnectingStepComponent: React.FC<Props> = (props): React.ReactElement => {
    const { connectionState, reconnectMessage, text, connectedContent, CheckConnectionStatus } = props;

    const [phase, setPhase] = React.useState<ConnectingStepPhase>('checking');

    const controllerRef = React.useRef<AbortController | undefined>(undefined);

    const runPrecheck = async (): Promise<void> => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        const { signal } = controller;

        setPhase('checking');

        try {
            const result = await CheckConnectionStatus(signal);
            if (signal.aborted) {
                return;
            }

            setPhase(result.outcome === 'status' ? 'ready' : result.outcome);
        } catch {
            if (signal.aborted) {
                return;
            }

            setPhase('unavailable');
        }
    };

    // Mount-only: the wizard mounts only while open, so the precheck runs once per entry.
    React.useEffect(() => {
        void runPrecheck();

        return () => controllerRef.current?.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (phase === 'checking') {
        return <LoadingIndicator active={true} showSpinner={true} centerIndicator={true} />;
    }

    if (phase === 'unavailable') {
        return (
            <div className={'page-stack'}>
                <Notification type={'danger'} heading={text?.fieldGuideUnreachable || 'We couldn\'t reach FieldGuide right now - try again in a few minutes.'} dismissable={false} />
                <div>
                    <Button type={'primary'} onClick={() => void runPrecheck()}>{text?.fieldGuideTryAgain || 'Try again'}</Button>
                </div>
            </div>
        );
    }

    if (phase === 'forbidden') {
        return <Notification type={'danger'} heading={text?.fieldGuideNoPermission
                || 'You don\'t have permission to send workbooks to FieldGuide. Ask your administrator for access.'} dismissable={false} />;
    }

    if (connectionState === FieldGuideConnectionState.Connected) {
        return <>{connectedContent}</>;
    }

    return (
        <ConnectStep
            reconnectBanner={reconnectMessage
                ? <Notification type={'warning'} heading={reconnectMessage} dismissable={false} />
                : undefined}
        />
    );
};

export const ConnectingStep = connect<InjectedReduxState, InjectedActionCreators, ConnectingStepProps, ApplicationState>(
    (appState: ApplicationState) => {
        return {
            connectionState: GetFieldGuideConnectionState(appState),
            reconnectMessage: GetFieldGuideReconnectMessage(appState),
            text: GetPortalText<PortalTextSubmissionDetails>(appState, 'submissionDetails'),
        };
    },
    (dispatch: SimplifiedThunkDispatch<FieldGuideKnownAction>) => {
        return {
            CheckConnectionStatus: (signal?: AbortSignal) => dispatch(FieldGuideStoreActions.CheckConnectionStatus(signal)),
        };
    }
)(ConnectingStepComponent);
